import * as React from "react";
import { useNavigate } from "@opendash/router";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

const grundwasserImage = require("../../assets/grundwasser.png");
const kgrundwasserImage = require("../../assets/kgrundwasser.png");
const illusImage = require("../../assets/illus.png");

export const WasserherkunftRoute = React.memo(function WasserherkunftRoute() {
  const navigate = useNavigate();
  const { t } = useTexts();

  const tabs = [
    {
      title: t("b.tab.1.title", "Die Bedeutung von Wasser"),
      path: "/wasserklaerer/wasserangebot/angebot",
      isActive: false,
    },
    {
      title: t("b.tab.2.title", "Wo kommt das Wasser her?"),
      path: "/wasserklaerer/wasserangebot/herkunft",
      isActive: true,
    },
    {
      title: t("b.tab.3.title", "Wasserverbrauch in Deutschland"),
      path: "/wasserklaerer/wasserangebot/verbrauch",
      isActive: false,
    },
    {
      title: t("b.tab.4.title", "Entwicklung des Wasserverbrauchs"),
      path: "/wasserklaerer/wasserangebot/entwicklung",
      isActive: false,
    },
  ];

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("tabs.herkunft", "Wo kommt das Wasser her")}</h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        <Card>
          <ImageContainer>
            <img
              src={illusImage}
              alt="Illustration der verschiedenen Wasserquellen"
            />
            <FigureCaption>
              {processInlineFormatting(
                t(
                  "wasserherkunft.illustration.caption",
                  "Abbildung 1: Wege des Wassers - Aus der Natur zum Wasserhahn (Illustration, eigene Darstellung, auf Basis von [1] [2] [3] [4] [5])"
                )
              )}
            </FigureCaption>
          </ImageContainer>
        </Card>

        <Card>
          <h2>
            {t(
              "wasserherkunft.grundwasser.title",
              "Grund- und Quellwasser (71%)"
            )}
          </h2>

          <h3>
            {t(
              "wasserherkunft.grundwasser.erklaert.title",
              "Grundwasser erkl\u00e4rt"
            )}
          </h3>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p1",
                "71 % unseres Trinkwassers stammt aus dem Grundwasservorkommen. [1]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p2",
                "Grundwasser entsteht vor allem aus Niederschlag. Davon versickert nur etwa ein Drittel, der Rest verdunstet oder flie\u00dft oberfl\u00e4chlich ab. [6]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p3",
                "Die Grundwasser f\u00fchrende Schicht kann von einigen Metern bis zu 100 m unterhalb des Bodens reichen. Der Boden \u00fcbernimmt dabei eine nat\u00fcrliche Filterfunktion, durch die Versickerung entsteht sauberes Grundwasser. [6] [7]"
              )
            )}
          </p>

          <ImageContainer>
            <img
              src={grundwasserImage}
              alt="Darstellung der Grundwasserentstehung und -gewinnung"
            />
            <FigureCaption>
              {processInlineFormatting(
                t(
                  "wasserherkunft.grundwasser.image.caption",
                  "Abbildung 2: Nat\u00fcrliche Versickerung des Niederschlags (KI-generierte Darstellung aus [6])"
                )
              )}
            </FigureCaption>
          </ImageContainer>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p4",
                "Gut durchl\u00e4ssige Boden- oder Gesteinsschichten, in denen sich Grundwasser bewegen oder sammeln kann, werden als Grundwasserleiter oder Aquifere bezeichnet. Diese Einheiten werden in der Regel nach unten durch wenig durchl\u00e4ssige Gesteinsschichten begrenzt, die als Grundwasserstauer bezeichnet werden. Sie verhindern das weitere Versickern des Wassers in tiefere geologische Schichten. [2]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p5",
                "Je l\u00e4nger der Flie\u00dfweg des Wassers durch mineralhaltiges Gestein ist und je gr\u00f6\u00dfer die Kontaktfl\u00e4che zum Speichermedium, desto intensiver k\u00f6nnen sich Mineralstoffe aus dem Gestein l\u00f6sen und ins Wasser \u00fcbergehen. So ist Tiefenwasser in der Regel mineralstoffreicher als oberfl\u00e4chennahes Grundwasser. [8]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.grundwasser.erklaert.p6",
                "Schlie\u00dflich wird das Grundwasser durch Brunnen gef\u00f6rdert und zum Wasserwerk gepumpt, wo es technische Aufbereitungsverfahren durchl\u00e4uft. An die Qualit\u00e4t des gef\u00f6rderten Wassers stellt Deutschland hohe Anforderungen. Die Verantwortung f\u00fcr die Trinkwasserqualit\u00e4t tragen die Wasserversorger. Diese wird zus\u00e4tzlich regelm\u00e4\u00dfig von den Gesundheits\u00e4mtern kontrolliert, um die Trinkwassersicherheit zu gew\u00e4hrleisten. [2] [3] [9]"
              )
            )}
          </p>

          <h3>
            {t("wasserherkunft.quellwasser.title", "Sonderfall Quellwasser")}
          </h3>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.quellwasser.p1",
                "Wird Grundwasser durch Brunnen oder Pumpwerke technisch gefasst und an die Oberfl\u00e4che gef\u00f6rdert, so bleibt es als Grundwasser klassifiziert, da die Entnahme direkt aus dem unterirdischen Speicher erfolgt. Gelangt das Grundwasser hingegen auf nat\u00fcrlichem Weg an die Oberfl\u00e4che wird es als Quellwasser bezeichnet. [2] [7] [10]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.quellwasser.p2",
                "Wasser aus einem Quellbach gilt genau genommen nicht mehr als reines Quellwasser, da es Niederschlag aufnehmen kann und somit den Oberfl\u00e4chengew\u00e4ssern zugeordnet wird. [2]"
              )
            )}
          </p>

          <h3>
            {t(
              "wasserherkunft.foerderung.title",
              "F\u00f6rderung des Quellwassers"
            )}
          </h3>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.foerderung.p1",
                "Der Grundwasserspiegel orientiert sich grob am Gel\u00e4ndeprofil. An Hanglagen oder Talr\u00e4ndern kann es daher vorkommen, dass der Grundwasserspiegel h\u00f6her liegt als die Gel\u00e4ndeoberfl\u00e4che \u2013 das Wasser tritt dann selbstst\u00e4ndig aus - als Quelle. In bestimmten F\u00e4llen \u2013 beispielsweise bei artesischen Quellen \u2013 kann zus\u00e4tzlich ein hydrostatischer Druck im Grundwasserleiter bestehen, der das Wasser sogar gegen das Gef\u00e4lle nach oben dr\u00fcckt, sodass es ohne technische Hilfsmittel an die Oberfl\u00e4che gelangt. [7] [10]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.foerderung.p3",
                "Quellen werden eingefasst, damit das nat\u00fcrlich austretende Rohwasser zur Aufbereitung weitergeleitet werden kann. Wasserschutzgebiete um Quellen und Brunnen herum stellen sicher, dass das gewonnene Wasser von \u00f6rtlichen Verschmutzungen frei bleibt. Aufgrund ihres Schutzes und der nat\u00fcrlichen Bodenfiltration kann beispielsweise ein Gro\u00dfteil des in Bayern gewonnenen Rohwassers ohne weitere Aufbereitung als Trinkwasser verwendet werden. [7]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "wasserherkunft.oberflaeche.title",
              "Oberfl\u00e4chenwasser: See-, Talsperren- und Flusswasser (13%)"
            )}
          </h2>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.definition.p1",
                "Oberfl\u00e4chenwasser ist Wasser aus oberirdischen, nat\u00fcrlichen oder k\u00fcnstlich angelegten Gew\u00e4ssern. [4]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.definition.kategorien",
                "Es wird in drei Kategorien eingeteilt [4]:"
              )
            )}
          </p>
          <ul>
            <li>Talsperrenwasser</li>
            <li>Seenwasser</li>
            <li>Flusswasser</li>
          </ul>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.definition.p2",
                "Rund 13 % des in Deutschland gewonnenen Trinkwassers stammt aus Oberfl\u00e4chenwasser. Damit ist jedoch ausschlie\u00dflich das direkt entnommene Wasser aus Fl\u00fcssen, Seen oder Talsperren gemeint, das in Wasserwerken mit technischen Verfahren aufbereitet wird, bevor es ins Trinkwassernetz gelangt. Nicht mitgez\u00e4hlt in diesen 13 % ist das sogenannte angereicherte Grundwasser \u2013 obwohl auch dieses urspr\u00fcnglich aus Oberfl\u00e4chengew\u00e4ssern stammt. [1]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.definition.p4",
                "In Bayern stammen j\u00e4hrlich rund 25 Mio. m\u00b3 des Trinkwassers aus Oberfl\u00e4chengew\u00e4ssern. Es stellt damit eine wichtige Ressource zur Sicherung der \u00f6ffentlichen Wasserversorgung dar. [4]"
              )
            )}
          </p>

          <h3>
            {t(
              "wasserherkunft.oberflaeche.kategorien.title",
              "Kategorien des Oberfl\u00e4chenwassers"
            )}
          </h3>

          <h4>
            {t(
              "wasserherkunft.oberflaeche.talsperre.title",
              "Talsperrenwasser:"
            )}
          </h4>
          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.talsperre.p1",
                "Talsperren sammeln Regen- und Flusswasser in gro\u00dfen Staubecken und helfen dadurch \u00dcberschwemmungen bei starkem Regen zu verhindern, indem sie Hochwasser zur\u00fcckhalten. In Trockenzeiten sorgen sie daf\u00fcr, dass genug Wasser in den Fl\u00fcssen bleibt. [11]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.talsperre.p2",
                "Eine weitere Aufgabe, die sie \u00fcbernehmen ist die Trinkwasserversorgung. Das Seewasser wird hierbei \u00fcber Entnahmet\u00fcrme aus tiefen Wasserschichten (\u2265 30 m Tiefe) entnommen. [4] Das liegt daran, dass das Wasser aus den tieferen Ebenen meist keimarmer und frei von Tr\u00fcbungsstoffen ist. Von der Qualit\u00e4t her ist das Wasser aus tieferen Wasserschichten dem Grundwasser \u00e4hnlich. [2]"
              )
            )}
          </p>

          <h4>
            {t("wasserherkunft.oberflaeche.see.title", "Seenwasser:")}
          </h4>
          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.see.p1",
                "Entnahme erfolgt \u00e4hnlich wie bei Talsperren, jedoch aus nat\u00fcrlichen Seen. Beispiel: Die Stadtwerke Lindau gewinnen Trinkwasser aus dem Bodensee in rund 60 m Tiefe. [4]"
              )
            )}
          </p>

          <h4>
            {t("wasserherkunft.oberflaeche.fluss.title", "Flusswasser:")}
          </h4>
          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.oberflaeche.fluss.p1",
                "Flusswasser wird \u00fcber quer zur Flie\u00dfrichtung angelegte Einlaufbauwerke oder Teiche entnommen. In Bayern hat Flusswasser eine geringe Bedeutung f\u00fcr die Trinkwasserversorgung; jedoch wird z. B. Donauwasser bei Leipheim von Baden-W\u00fcrttemberg verwendet. [4]"
              )
            )}
          </p>
        </Card>

        <Card>
          <h2>
            {t(
              "wasserherkunft.angereichert.title",
              "Angereichertes Grundwasser und Uferfiltrate (16%)"
            )}
          </h2>

          <h3>
            {t("wasserherkunft.angereichert.grundlagen.title", "Grundlagen")}
          </h3>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.angereichert.grundlagen.p1",
                "In Ballungsr\u00e4umen \u2013 etwa im Ruhrgebiet \u2013 besteht ein hohes Wasseraufkommen, dem nicht immer ausreichende Grundwasserressourcen gegen\u00fcberstehen. Eine L\u00f6sung besteht darin diese Vorkommen k\u00fcnstlich anzureichen. Dieser Prozess wird k\u00fcnstliche Grundwasseranreicherung genannt. Hierbei wird Oberfl\u00e4chenwasser gezielt entweder durch nat\u00fcrliche oder k\u00fcnstlich geschaffene Wege in den Grundwasserleiter geleitet. Indem das Wasser durch Sandschichten in den Untergrund sickert, wird das Wasser auf nat\u00fcrliche Art und Weise gereinigt. Es wird gemeinsam mit dem nat\u00fcrlichen Grundwasser \u00fcber Brunnen gef\u00f6rdert und zur weiteren Aufbereitung in das Wasserwerk geleitet. [5]"
              )
            )}
          </p>

          <InfoBox>
            <h4>
              {t(
                "wasserherkunft.angereichert.beispiel.title",
                "Beispiel Wasserwerk Haltern"
              )}
            </h4>
            <p>
              {processInlineFormatting(
                t(
                  "wasserherkunft.angereichert.beispiel.text",
                  "Von rund 100 Mio. m\u00b3 abgegebenem Wasser pro Jahr stammen etwa 70 % aus k\u00fcnstlich angereichertem Grundwasser. [5]"
                )
              )}
            </p>
          </InfoBox>

          <h3>
            {t(
              "wasserherkunft.angereichert.methodik.title",
              "Methodik der Anreicherung und Sonderfall Uferfiltration"
            )}
          </h3>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.angereichert.methodik.p1",
                "Die k\u00fcnstliche Grundwasseranreicherung erfolgt typischerweise \u00fcber Versickerungsbecken. Dabei wird Oberfl\u00e4chenwasser \u2013 z. B. aus Fl\u00fcssen oder Talsperren \u2013 kontrolliert zur Versickerung gebracht. Beim Durchstr\u00f6men der Bodenschichten erf\u00e4hrt das Wasser eine nat\u00fcrliche Filterung und Reinigung. [5]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.angereichert.methodik.p2",
                "Ein Sonderfall stellt die Uferfiltration dar: Hierbei gelangt Wasser aus einem Fluss oder See in den angrenzenden Grundwasserleiter, entweder nat\u00fcrlich oder durch gezielte Steuerung. Auch in diesem Fall \u00fcbernimmt der Untergrund die Funktion eines nat\u00fcrlichen Filters. Das filtrierte Wasser wird gemeinsam mit dem Grundwasser \u00fcber Brunnen in Ufern\u00e4he entnommen. [5]"
              )
            )}
          </p>

          <p>
            {processInlineFormatting(
              t(
                "wasserherkunft.angereichert.methodik.p3",
                "F\u00fcr eine wirksame Uferfiltration m\u00fcssen bestimmte Standortbedingungen erf\u00fcllt sein [5]:"
              )
            )}
          </p>
          <ul>
            <li>
              ein ausreichend wasserf\u00fchrendes Gew\u00e4sser in der nahen Umgebung
              des Brunnens,
            </li>
            <li>ein durchl\u00e4ssiger Untergrund (z. B. Sand oder Kies),</li>
            <li>
              ein guter hydraulischer Kontakt zwischen Gew\u00e4sser und
              Grundwasserleiter.
            </li>
          </ul>

          <ImageContainer>
            <img
              src={kgrundwasserImage}
              alt="K\u00fcnstliche Grundwasseranreicherung"
            />
            <FigureCaption>
              {t(
                "wasserherkunft.angereichert.image.caption",
                "Abbildung 3: K\u00fcnstliche Grundwasseranreicherung: naturnahes Verfahren zur Wassergewinnung (Illustration, KI-generiert)"
              )}
            </FigureCaption>
          </ImageContainer>
        </Card>

        <SourcesSection>
          <h3>{t("wasserherkunft.literatur.title", "Literaturverzeichnis")}</h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref1",
                  `[1] Umweltbundesamt (Hrsg.), \u201E\u00d6ffentliche Wasserversorgung\u201C, Umweltbundesamt, 2024. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/daten/wasser/wasserwirtschaft/oeffentliche-wasserversorgung`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref2",
                  `[2] BMLUK (Hrsg.), \u201EGrund-, Trink-, Quellwasser\u201C, Generationblue. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.generationblue.at/wasserwissen/wasserqualitaet/grund-trink-quellwasser.html`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-3">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref3",
                  `[3] H. Bartel, H. H. Dieter, I. Feuerpfeil, H-J. Grummt, T. Grummt, A. Hummel, et al., \u201ERund um das Trinkwasser\u201C, Dessau-Ro\u00dflau: Umweltbundesamt, 2016. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/uba_rund_um_das_trinkwasser_ratgeber_web_0.pdf`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-4">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref4",
                  `[4] Bayerisches Landesamt f\u00fcr Umwelt (Hrsg.), \u201EOberfl\u00e4chenwasser\u201C, lfu bayern. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.lfu.bayern.de/wasser/trinkwasser_quelle_verbraucher/trinkwassergewinnung/oberflaechenwasser/index.htm`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-5">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref5",
                  `[5] M. Roszinsky, \u201EK\u00fcnstliche Grundwasseranreicherung: naturnahes Verfahren zur Wassergewinnung\u201C, Gelsenwasser-Blog, 2021. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.gelsenwasser-blog.de/kuenstliche-grundwasseranreicherung-naturnahes-verfahren-zur-wassergewinnung/`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-6">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref6",
                  `[6] M. Kessler, \u201EWie das Grundwasser immer knapper wird\u201C, zdfheute, 2023. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.zdfheute.de/wissen/grundwasser-duerre-trinkwasser-deutschland-wasser-100.html`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-7">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref7",
                  `[7] Bayerisches Landesamt f\u00fcr Umwelt (Hrsg.), \u201EGrundwasser wird zu Trinkwasser\u201C, lfu bayern. Zugegriffen: 26. November 2025. [Online]. Verf\u00fcgbar unter: https://www.lfu.bayern.de/wasser/agws/gwwissen/gw_wird_zu_tw/index.htm`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-8">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref8",
                  `[8] C. Baumeister, J. Kampf, Dr. S. Sauer, B. Winternheimer, und T. Schollmayer, \u201EWASSERVERSORGUNG IN RHEINLAND-PFALZ - Vom Grundwasser zum Trinkwasser\u201C, Mainz: Landesamt f\u00fcr Umwelt Rheinland-Pfalz (LfU), 2022. [Online]. Verf\u00fcgbar unter: https://lfu.rlp.de/fileadmin/lfu/Service/Publikationen/Schutz_der_Bevoelkerung/Broschueren_und_Berichte/Wasserversorgung/Wasserversorgung_in_RLP_2022.pdf`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-9">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref9",
                  `[9] Umweltbundesamt (Hrsg.), \u201ETrinkwasser\u201C, Umweltbundesamt. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/themen/wasser/trinkwasser`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-10">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref10",
                  `[10] Bayerisches Landesamt f\u00fcr Umwelt (Hrsg.), \u201EQuellen\u201C, lfu bayern. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.lfu.bayern.de/wasser/trinkwasser_quelle_verbraucher/trinkwassergewinnung/quellen/index.htm`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-11">
              <LinkifyText>
                {t(
                  "wasserherkunft.literatur.ref11",
                  `[11] Ministerium f\u00fcr Umwelt, Naturschutz und Verkehr des Landes Nordrhein-Westfalen, \u201ETalsperren und Stauanlagen\u201C, umwelt.nrw. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.umwelt.nrw.de/themen/umwelt/umwelt-und-wasser/gewaesser/talsperren-und-stauanlagen`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
